import { getEnvironment } from '@/shared/config';
import { initializeSentry, closeSentry } from '@/shared/lib/sentry';
import { cookieConsentService } from '@/shared/ui/cookie-consent';
import { logger } from '@/shared/utils/logger';
import { scrubSentryEvent } from '@/shared/utils/sentryScrub';

/**
 * Check if user has consented to analytics cookies
 */
export const checkAnalyticsConsent = (): boolean => {
  return cookieConsentService.hasConsent('analytics');
};

export const initializeAnalytics = async () => {
  // Check for consent before initializing
  if (!checkAnalyticsConsent()) {
    logger.info('Analytics not initialized - no consent');
    return;
  }

  const env = getEnvironment();
  const isDevelopment = env === 'development';
  const isProduction = env === 'production';

  // Only initialize if we have consent and not in development
  if (!isDevelopment) {
    const initialized = await initializeSentry({
      enabled: true,
      environment: env,
      tracesSampleRate: isProduction ? 0.1 : 1.0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      beforeSend(event, hint) {
        // Double-check consent before sending
        if (!checkAnalyticsConsent()) {
          return null;
        }

        const error = hint.originalException;

        // Browser geolocation rejections are expected user/device states, not
        // application errors. The browser delivers them as either a real
        // GeolocationPositionError or our normalized `{ code, message }` shape
        // from locationService.ts. Drop them at the source so they don't ride
        // through as `[object GeolocationPositionError]` or `Error: Timeout`.
        if (error && typeof error === 'object') {
          if (
            typeof window !== 'undefined' &&
            typeof window.GeolocationPositionError === 'function' &&
            error instanceof window.GeolocationPositionError
          ) {
            return null;
          }
          if ('code' in error) {
            const geoCode = (error as { code?: unknown }).code;
            if (
              geoCode === 'PERMISSION_DENIED' ||
              geoCode === 'POSITION_UNAVAILABLE' ||
              geoCode === 'TIMEOUT' ||
              geoCode === 'NOT_SUPPORTED'
            ) {
              return null;
            }
          }
        }

        // Filter non-Error objects (e.g., Auth0 throws plain objects like { error: 'login_required' })
        if (error && typeof error === 'object' && !(error instanceof Error) && 'error' in error) {
          const authError = (error as { error: string }).error;
          if (authError === 'login_required' || authError === 'timeout' || authError === 'invalid_grant') {
            return null;
          }
        }

        // Filter out expected/transient errors that don't require action.
        // Some SDK/browser errors are emitted as plain event messages instead
        // of Error instances, so we normalize all available message sources.
        const ignoredPatterns = [
          'Network request failed', // Network connectivity issues
          'Network Error', // Axios network errors
          'websocket error', // Socket.io transient errors
          'Socket error', // Socket connection issues
          'Permission was denied', // User denied browser permission (expected)
          'NotAllowedError', // Browser permission denied
          'Unable to preload', // CSS/JS preload failures (stale cache)
          'not a valid JavaScript MIME type', // Stale asset requests
          'Load failed', // Resource load failures
          'Failed to fetch', // Fetch API network errors
          'timeout', // Request timeouts (case insensitive check below)
          'login_required', // Auth0 session expired (normalized from plain object)
          'login required', // Auth0 session expired (normalized SDK Error message)
          'invalid_grant', // Auth0 token expired
          'feature named `pageObserver` was not found', // Instrumentation feature lookup noise
          'feature named `hover` was not found', // Instrumentation feature lookup noise
          'GeolocationPositionError', // Browser geolocation API rejection
          '[object GeolocationPositionError]', // Stringified GeolocationPositionError from older SDKs
          'Location permission was denied', // Our normalized geolocation message
          'Location permission was previously denied', // Our normalized geolocation message
          'Position unavailable', // Our normalized geolocation message
          'Unable to determine your location', // Our normalized geolocation message
          'Geolocation is not supported', // Our normalized geolocation message
        ];

        const candidateMessages: string[] = [];
        if (error instanceof Error) {
          if (error.message) candidateMessages.push(error.message);
          if (error.name) candidateMessages.push(error.name);
        } else if (typeof error === 'string') {
          candidateMessages.push(error);
        }

        if (typeof event.message === 'string') {
          candidateMessages.push(event.message);
        }

        const exceptionMessage = event.exception?.values?.[0]?.value;
        if (typeof exceptionMessage === 'string') {
          candidateMessages.push(exceptionMessage);
        }

        const shouldIgnore = candidateMessages.some(message =>
          ignoredPatterns.some(pattern => message.toLowerCase().includes(pattern.toLowerCase())),
        );

        if (shouldIgnore) {
          return null;
        }

        // Defense-in-depth: redact JWT-shape strings and any `token` field
        // from the event payload (request body, exception messages,
        // breadcrumbs, etc.) before it leaves the browser. Catches review
        // tokens that an axios error may have captured from a leaked
        // backend response.
        // Fail-closed: scrubber returns null on internal error → drop event.
        return scrubSentryEvent(event);
      },
    });

    if (initialized) {
      logger.info('Analytics initialized with consent');
    }
  }
};

// Re-initialize analytics when consent changes
export const updateAnalyticsConsent = (hasConsent: boolean) => {
  if (hasConsent) {
    initializeAnalytics();
  } else {
    // Disable Sentry if consent is revoked
    closeSentry();
    logger.info('Analytics disabled - consent revoked');
  }
};
